import { Icon } from "~/components/ui/icons/icon";
import { useInstrumentation } from "~/hooks/useInstrumentation";
import { getLinkIcon, getLinkLabel } from "~/lib/links";
import { cn } from "~/lib/utils";

interface PlaceContactLinksProps {
  links?: string[];
  email?: string | null;
  phone?: string | null;
  className?: string;
  placeId?: string;
  context?: "adventure_map" | "landing_page";
}

/**
 * Displays contact info (email, phone) and external links for a place.
 * Shows brand icons and labels for known services (Instagram, Google Maps, etc.).
 */
export function PlaceContactLinks({ links, email, phone, className, placeId, context }: PlaceContactLinksProps) {
  const instrumentation = useInstrumentation();
  const hasContact = email || phone;
  const validLinks = links?.filter(isValidHttpUrl) ?? [];
  const hasLinks = validLinks.length > 0;

  const trackLinkClick = (linkType: "phone" | "email" | "link") => {
    if (placeId && context) {
      instrumentation.track("External Link Clicked", { placeId, linkType, context });
    }
  };

  if (!hasContact && !hasLinks) return null;

  return (
    <div className={cn("flex flex-col gap-2 text-sm", className)}>
      {phone && (
        <a
          href={`tel:${phone}`}
          aria-label={`Call ${phone}`}
          onClick={() => trackLinkClick("phone")}
          className="group inline-flex items-center gap-2 text-secondary hover:text-foreground transition-colors w-fit"
        >
          <Icon i="phone" className="size-4 opacity-60" />
          <span className="group-hover:underline underline-offset-2">{phone}</span>
        </a>
      )}
      {email && (
        <a
          href={`mailto:${email}`}
          aria-label={`Email ${email}`}
          onClick={() => trackLinkClick("email")}
          className="group inline-flex items-center gap-2 text-secondary hover:text-foreground transition-colors w-fit"
        >
          <Icon i="email" className="size-4 opacity-60" />
          <span className="group-hover:underline underline-offset-2 truncate max-w-64">{email}</span>
        </a>
      )}

      {hasLinks &&
        validLinks.map((url) => (
          <a
            key={url}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Visit ${getLinkLabel(url)}`}
            onClick={() => trackLinkClick("link")}
            className="group inline-flex items-center gap-2 text-secondary hover:text-foreground transition-colors w-fit"
          >
            <Icon i={getLinkIcon(url)} className="size-4" />
            <span className="group-hover:underline underline-offset-2 truncate max-w-56">{getLinkLabel(url)}</span>
          </a>
        ))}
    </div>
  );
}

function isValidHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ["http:", "https:"].includes(parsed.protocol);
  } catch {
    return false;
  }
}
